import React, {useEffect, useState, useRef} from 'react';
import { useNavigate } from 'react-router-dom';
import LoaderBtn from '../Assets/LoaderBtn';
import { PrimaryBtn } from '../Assets/Buttons';
import { useGetUserSubscriptionQuery, useCreateCustomerPortalSessionMutation } from '../../services/api/userApi';

const Subscriptions = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [subscriptionLoader, setSubscriptionLoader] = useState(true);
    const { data: subscriptionData, isLoading: isSubscriptionLoading, isError: isSubscriptionError, isFetching: isSubscriptionFetching } = useGetUserSubscriptionQuery();
    const [createCustomerPortalSession, { data: sessionData, isLoading: isCreatingSession, isError: isSessionError, error: sessionError }] = useCreateCustomerPortalSessionMutation();

    const loadingStartTimeRef = useRef(null);

    // Run when subscription data is loaded, show spinner for minimum .5 second
    useEffect(() => {
        // When loading starts, record the start time
        if (isSubscriptionLoading || isSubscriptionFetching) {
            if (!loadingStartTimeRef.current) {
                loadingStartTimeRef.current = Date.now();
                setSubscriptionLoader(true);
            }
        }
        
        // When loading finishes
        if (!isSubscriptionLoading && !isSubscriptionFetching && subscriptionData !== undefined) {
            const elapsedTime = loadingStartTimeRef.current 
                ? Date.now() - loadingStartTimeRef.current 
                : 0;
            const remainingTime = Math.max(500 - elapsedTime, 0);
            
            const timeoutId = setTimeout(() => {
                setSubscriptionLoader(false);
                loadingStartTimeRef.current = null; // Reset for next time
            }, remainingTime);
    
            return () => clearTimeout(timeoutId);
        }
    }, [isSubscriptionLoading, isSubscriptionFetching, subscriptionData]);

    // Converts to Stripe timestamp to a readable date
    const convertTimestampToDate = (timestamp) => {
        const date = new Date(timestamp * 1000); // Convert from seconds to milliseconds
        return date.toLocaleString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric'
        });
    };

    // Function to create a customer portal session and redirect them to it
    const manageSubscription = async () => {
        setIsLoading(true);

        try {
            const body = { 
                return_url: `${window.location.origin}/profile?portal_session=completed`,
            };
            
            const data = await createCustomerPortalSession(body).unwrap();
            
            const portalUrl = data.url;
            window.location.href = portalUrl; // Redirect to Stripe's customer portal
        } catch (error) {
            console.error('Error creating portal session:', error);
            setIsLoading(false);
        }
    };

    return (
        <div className="rounded-lg overflow-hidden">
            <div className="bg-gray-50 dark:bg-brand-dark-700 px-4 py-3 border-b border-gray-200 dark:border-brand-dark-600">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    My Subscriptions
                </h3>
            </div>
            <div className="bg-white dark:bg-brand-dark-800 px-4 py-5 space-y-4">
                {(subscriptionData && !isSubscriptionLoading && !isSubscriptionError && !subscriptionLoader) && 
                    (
                        <div className="space-y-4">
                            <p className="text-gray-700 dark:text-gray-300">
                                <span className="font-medium">${subscriptionData.amount / 100}</span> USD / {subscriptionData.interval}
                            </p>
                            <p className="text-gray-700 dark:text-gray-300">
                                Status: <span className="font-medium">{subscriptionData.subscription_status}</span>
                            </p>
                            <p className="text-gray-700 dark:text-gray-300">
                                {subscriptionData.subscription_status === 'canceled' ? 'Subscription End Date: ' : 'Next Bill Date: '}
                                <span className="font-medium">{convertTimestampToDate(subscriptionData.current_period_end)}</span>
                            </p>
                            <LoaderBtn
                                btnClasses="inline-flex items-center justify-center rounded-md border border-transparent bg-brand-red-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-red-600 focus:outline-none focus:ring-2 focus:ring-brand-red-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                isLoading={isLoading}
                                handler={manageSubscription}
                                defaultText="Manage Subscription"
                                loadingText="Loading"
                            />
                        </div>
                    )
                }
                {(!subscriptionData && !isSubscriptionLoading && !isSubscriptionError) && 
                    (
                        <div className="space-y-4">
                            <p className="text-gray-700 dark:text-gray-300">No active subscriptions</p>
                            <PrimaryBtn
                                text="View Pricing"
                                onClick={() => navigate('/pricing')}
                            />
                        </div>
                    )
                }
                {subscriptionLoader && 
                    <div className="space-y-4 w-full m rounded-md ">
                        <div className="flex animate-pulse space-x-4">
                            <div className="flex-1 space-y-6 py-1">
                                <div className="h-2 rounded bg-gray-200"></div>
                                <div className="h-2 rounded bg-gray-200"></div>
                                <div className="h-2 rounded bg-gray-200"></div>
                                <div className="h-10 w-40 rounded bg-gray-200"></div>
                            </div>
                        </div>
                    </div>
                }
                {isSubscriptionError && 
                    <div className="space-y-4">
                        <p className="text-gray-700 dark:text-gray-300">Error loading subscription data</p>
                    </div>
                }
            </div>
        </div>
    );
};

export default Subscriptions;